import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import type {PosterProductDataForProcessDesign} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {PublishingAnalyticsAction} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {ThumbNailType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {Text, TextSize} from '@Components/text';
import {DigitalSignageSubPanel} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-sub-panel/digital-signage-sub-panel';
import {PublishingOptionListItem} from '@Panels/poster-publishing-options-panel/components/publishing-option-list-item/publishing-option-list-item';
import {Button, Size, Type} from '@Components/button';
import {setErrorMessageForPublishing, setErrorOnPublishPanel} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {usePosterProduct, usePrintProduct} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import type {DripBoardsScreens, PublishingDripboardsPanelProps, PublishingDripBoardsScreensResponse} from './publishing-dripboards-panel.types';
import {DripBoardsPanelProgressState} from './publishing-dripboards-panel.types';
import {useAppDispatch} from '@/hooks';
import styles from './publishing-dripboards-panel.module.scss';
import {GA4EventParamName} from '@Libraries/ga-events';

export function PublishingDripboardsPanel({...props}: PublishingDripboardsPanelProps): ReactElement<any> {
  const [selectedScreen, setSelectedScreen] = useState<DripBoardsScreens>();
  const [screens, setScreens] = useState<DripBoardsScreens[]>([{id: 0, name: 'Sarim'}]);
  const [isLoading, setIsLoading] = useState(false);
  const [panelFlow, setPanelFlow] = useState<DripBoardsPanelProgressState>(DripBoardsPanelProgressState.STEP_CONNECT);
  const dispatch = useAppDispatch();

  const buttonForDone = (): ReactElement<any> => {
    return <Button isFullWidth type={Type.PRIMARY} size={Size.SMALL} text={window.i18next.t('pmwjs_done')} onClick={props.onClose} />;
  };
  const premiumProductDataForPoster = usePosterProduct();
  const premiumProductDataForPrint = usePrintProduct();

  const productUsed: PosterProductDataForProcessDesign | undefined =
    premiumProductDataForPoster && Object.keys(premiumProductDataForPoster).length > 0 && premiumProductDataForPoster.id !== 0
      ? premiumProductDataForPoster
      : premiumProductDataForPrint;

  useEffect(() => {
    displayDripBoardsScreens();
  }, []);

  /**
   * Get user screens available on DripBoards and move to selection panel
   */
  const displayDripBoardsScreens = (): void => {
    setIsLoading(true);

    void window.PMW.readLocal('dripboards/getScreensNew')
      .then((response: PublishingDripBoardsScreensResponse) => {
        if (!response.isNewUser && response.screens) {
          setScreens(response.screens);
          setPanelFlow(DripBoardsPanelProgressState.STEP_AUTH);
        }
        setIsLoading(false);
      })
      .catch(() => {
        dispatch(setErrorOnPublishPanel(true));
        dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Subscribes the user to DripBoards and moves to screen selection panel
   */
  const subscribeToDripBoards = (): void => {
    console.log('came inside subscribe');
    setIsLoading(true);
    void window.PMW.writeLocal('dripboards/subscribeUser', {}).then((response) => {
      console.log('subscribe response: ', response);
      setPanelFlow(DripBoardsPanelProgressState.STEP_AUTH);
      setIsLoading(false);
    });
  };

  /**
   * Publishes the selected design to DripBoards
   */
  const publishToDripBoards = (): void => {
    if (!selectedScreen) {
      return;
    }
    setPanelFlow(DripBoardsPanelProgressState.STEP_LOADING);

    const screenId = selectedScreen.id;
    void window.PMW.writeLocal('dripboards/publishDesign', {
      idScreen: screenId,
      idPoster: productUsed?.hashedId,
      idOrder: productUsed?.orderId,
    }).then(() => {
      setPanelFlow(DripBoardsPanelProgressState.STEP_PUBLISH);
      window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE_PUBLISH, {[GA4EventParamName.TYPE]: 'DripBoards'});
    });
  };

  const dripboardsAuthenticatedPanel = (): ReactElement<any> => {
    return (
      <div>
        <div className={`${window.PMW.isReactPosterMaker() ? styles.screensDisplayHeightForDropDown : styles.screensDisplayHeightForPanel} spacing-m-b-6 _overflow-auto`}>
          {screens.map((screen) => {
            return (
              <PublishingOptionListItem
                customThumbnailClasses={`${selectedScreen?.id === screen.id ? 'bg-primary-10 content-primary' : 'bg-neutral-3 content-body'} `}
                iconName="icon-video-in-page"
                heading={screen.name}
                isClicked={selectedScreen?.id === screen.id}
                thumbNailType={ThumbNailType.ICON}
                onClick={() => {
                  setSelectedScreen(screen);
                }}
              />
            );
          })}
        </div>
        <Button onClick={publishToDripBoards} isFullWidth text={window.i18next.t('pmwjs_dripboards_publish')} type={Type.PRIMARY} disabled={!selectedScreen} />
      </div>
    );
  };
  const getDisplayPanel = (): ReactElement<any> => {
    switch (panelFlow) {
      case DripBoardsPanelProgressState.STEP_CONNECT:
        return (
          <DigitalSignageSubPanel
            panelHeading={window.i18next.t('pmwjs_dripboards_connect')}
            heading=""
            subheading={window.i18next.t('pmwjs_dripboards_subscribe_message')}
            showButton
            illustration="illustration-digital-signage.png"
            showCheckBox
            checkBoxText={window.i18next.t('pmwjs_dripboards_terms_and_privacy')}
            buttonText={window.i18next.t('pmwjs_dripboards_connect')}
            onClick={subscribeToDripBoards}
            isLoading={isLoading}
            onClose={props.onClose}
          />
        );
      case DripBoardsPanelProgressState.STEP_AUTH:
        return (
          <DigitalSignageSubPanel
            panelHeading={window.i18next.t('pmwjs_dripboards')}
            heading={window.i18next.t('pmwjs_dripboards_select_list')}
            listItem={dripboardsAuthenticatedPanel()}
            isLoading={isLoading}
            onClose={props.onClose}
          />
        );

      case DripBoardsPanelProgressState.STEP_LOADING:
        return (
          <DigitalSignageSubPanel
            panelHeading={window.i18next.t('pmwjs_publishing_no_dots')}
            heading=""
            isLoading
            loadingScreenText={window.i18next.t('pmwjs_dripboards_publishing')}
            onClose={props.onClose}
            contentClassName="-text-align-center"
          />
        );
      case DripBoardsPanelProgressState.STEP_PUBLISH:
        return (
          <DigitalSignageSubPanel
            illustration="publishing-dripboards-screens.png"
            headerClassName="flex-center"
            panelHeading={window.i18next.t('pmwjs_published')}
            heading=""
            listItem={
              <div className="flex-column-align-center">
                <Text
                  dangerouslySetInnerHTML
                  size={TextSize.SMALL}
                  className="content-body spacing-m-b-4 -text-align-center"
                  val={window.i18next.t('pmwjs_dripboards_success_message')}
                />
                <Text size={TextSize.XSMALL} className="content-sub-text" val={window.i18next.t('pmwjs_dripboards_publish_success_desc')} />
              </div>
            }
            showButton
            buttonComponent={buttonForDone()}
            onClose={props.onClose}
          />
        );
      default:
        return <div />;
    }
  };

  return getDisplayPanel();
}
